import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const INITIAL_PAGE_INFO = { currentPage: 0, tokens: [] };

/**
 * 토큰(nextToken) 기반 목록의 페이지 상태·조회 계기를 관리하는 훅
 *
 * 조회 계기는 두 가지로 나뉜다. 페이지 상태 자체는 조회 계기가 아니다.
 *   1) 조건 변경: fetchPage 참조가 바뀌면 1페이지로 초기화한 뒤 조회한다.
 *      URL(searchQueryParams) 조건은 한 렌더 늦게 params 에 반영되므로, 페이지 state 를 계기로 삼으면
 *      '이전 조건 + 1페이지' 요청이 함께 나간다.
 *   2) 페이지 이동·새로고침: 핸들러가 해당 페이지의 토큰으로 직접 조회한다.
 *
 * 새 조회가 시작되거나 조건이 바뀌거나 enabled 가 꺼지면 진행 중인 조회는 abort 한다. fetchPage 가 signal 을
 * API 까지 넘기지 않아도 늦게 도착한 응답이 토큰을 덮어쓰지는 않지만, 목록 데이터까지 보호하려면 signal 을 넘겨야 한다.
 *
 * @param {object} options
 * @param {({ nextToken: string|undefined, signal: AbortSignal }) => Promise<string|null|undefined>} options.fetchPage
 *   한 페이지를 조회하고 다음 토큰을 반환한다. 조회 조건에만 의존하는 useCallback 이어야 한다 —
 *   렌더마다 참조가 바뀌면 조건 변경으로 간주되어 무한 조회된다.
 *   실패 표시는 호출 측 책임(모듈 error·인터셉터 토스트)이므로 여기서는 삼킨다.
 * @param {boolean} [options.enabled=true] false 면 조회하지 않는다 (선행 데이터 로드 전 진입 게이트).
 *   true 로 바뀌면 1페이지부터 조회한다.
 * @returns {{ currentPage: number, refresh: () => Promise<void>, paginationProps: object }}
 *   refresh 는 현재 조건·현재 페이지로 다시 조회한다 — 변경 작업 후 호출용. 조건·페이지가 바뀌면 참조가 바뀌므로
 *   refresh 를 호출하는 useCallback 은 deps 에 refresh 를 넣어야 한다.
 *   paginationProps 는 Pagination 에 펼쳐 넘긴다.
 */
const useTokenPagination = ({ fetchPage, enabled = true }) => {
    const [pageInfo, setPageInfo] = useState(INITIAL_PAGE_INFO);
    const controllerRef = useRef(null);

    const load = useCallback(
        async (page, nextToken) => {
            controllerRef.current?.abort();
            const controller = new AbortController();
            controllerRef.current = controller;

            try {
                const token = await fetchPage({ nextToken, signal: controller.signal });
                if (controller.signal.aborted) return;
                setPageInfo((prev) => {
                    const tokens = [...prev.tokens];
                    tokens[page] = token ?? null;
                    return { ...prev, tokens };
                });
            } catch (e) {
                // 취소·실패 모두 토큰을 갱신하지 않는다 (실패 표시는 호출 측 책임)
            }
        },
        [fetchPage],
    );

    useEffect(() => {
        if (!enabled) return undefined;
        setPageInfo(INITIAL_PAGE_INFO);
        load(0);
        return () => controllerRef.current?.abort();
    }, [load, enabled]);

    const onChangePage = useCallback(
        (page) => {
            setPageInfo((prev) => ({ ...prev, currentPage: page }));
            return load(page, page > 0 ? pageInfo.tokens[page - 1] : undefined);
        },
        [load, pageInfo.tokens],
    );

    const refresh = useCallback(() => onChangePage(pageInfo.currentPage), [onChangePage, pageInfo.currentPage]);

    const paginationProps = useMemo(
        () => ({
            page: pageInfo.currentPage,
            hasNextToken: !!pageInfo.tokens[pageInfo.currentPage],
            onChangePage,
            onClickRefresh: refresh,
        }),
        [pageInfo, onChangePage, refresh],
    );

    return { currentPage: pageInfo.currentPage, refresh, paginationProps };
};

export default useTokenPagination;
